/**
 * 포트폴리오 본문에 노출 가능한 섹션 종류.
 * - `"rewards"`  : CROSS Rewards
 * - `"points"`   : CROSS Points (게임 토큰 예치 퀘스트, 내장 Withdraw)
 * - `"staking"`  : CROSS Staking
 * - `"gameSwap"` : Gametoken LP
 * - `"forge"`    : Forge
 * - `"crossdPool"` : CROSSD v3 pool 포지션, 내장 Withdraw
 *
 * `WalletPortfolioBody` / `WalletInfo`의 섹션 필터에 사용한다.
 */
export type PortfolioSection =
  | "rewards"
  | "points"
  | "staking"
  | "gameSwap"
  | "forge"
  | "crossdPool";

/**
 * 섹션 필터를 지정하지 않았을 때(=전체 노출)의 기본 표시 순서.
 * 배열로 필터를 줄 때 어떤 키들이 유효한지에 대한 단일 출처(source of truth).
 */
export const PORTFOLIO_SECTIONS: readonly PortfolioSection[] = [
  "rewards",
  "points",
  "staking",
  "gameSwap",
  "forge",
  "crossdPool",
];

/**
 * 포트폴리오/Send 기능에서 트랜잭션을 실제로 서명/브로드캐스트할 때
 * 호출하는 공통 payload 타입.
 *
 * 주입되는 지갑 스택(wagmi, viem, 자체 SDK 등)에 관계없이 동일한 계약으로
 * 동작시키기 위한 최소 공통 시그니처다. wagmi의 `sendTransactionAsync`를
 * 그대로 전달해도 호환된다.
 */
export interface SendTransactionArgs {
  to: `0x${string}`;
  value?: bigint;
  data?: `0x${string}`;
  chainId?: number;
  gas?: bigint;
  maxFeePerGas?: bigint;
  maxPriorityFeePerGas?: bigint;
}

export type SendTransactionFn = (
  args: SendTransactionArgs,
) => Promise<`0x${string}`>;

export interface GetTransactionReceiptArgs {
  hash: `0x${string}`;
  chainId?: number;
}

export interface TransactionReceiptResult {
  status?: "success" | "reverted" | "0x1" | "0x0" | number | bigint | boolean;
}

export type GetTransactionReceiptFn = (
  args: GetTransactionReceiptArgs,
) => Promise<TransactionReceiptResult | null | undefined>;

/**
 * Send 확인 단계에서 표시할 가스/수수료 추정 정보를 가져오는 콜백.
 *
 * Send 확인 화면 표기 정책 (crossy-sdk-js `docs/06-gas-fee.md` 기준):
 * - `maxFeePerGas` 채움 → Dynamic(EIP-1559) 모드로 표기
 *   · Est. Tx Fee = `gasLimit × maxFeePerGas` (native)
 *   · Max Priority Fee = `maxPriorityFeePerGas` (Gwei)
 *   · Max Gas Fee = `maxFeePerGas` (Gwei)
 *   · Gas Limit = `gasLimit`
 * - `gasPrice`만 채움 → Legacy 모드로 표기
 *   · Est. Tx Fee = `gasLimit × gasPrice` (native)
 *   · Gas Price = `gasPrice` (Gwei)
 *   · Gas Limit = `gasLimit`
 *
 * 둘 다 채워지면 Dynamic 우선. 둘 다 비어있으면 해당 행은 "—"로 표시된다.
 */
export interface EstimateGasArgs {
  to: `0x${string}`;
  value?: bigint;
  data?: `0x${string}`;
  chainId?: number;
  from?: `0x${string}`;
}

export interface GasEstimate {
  gasLimit: bigint;
  gasPrice?: bigint;
  maxFeePerGas?: bigint;
  maxPriorityFeePerGas?: bigint;
}

export type EstimateGasFn = (args: EstimateGasArgs) => Promise<GasEstimate>;

export interface TokenBalance {
  blockNumber: number;
  name: string;
  symbol: string;
  chainId: number;
  address: string;
  quantity: {
    decimals: number;
    numeric: string;
  };
  icon_url: string;
}

export interface TokenBalanceResponse {
  code: number;
  message: string;
  data: TokenBalance[];
}

export interface TokenStats {
  chain_id: number;
  address: string;
  price: string;
  percent_change_24h: string;
}

export interface TokenStatsResponse {
  code: number;
  message: string;
  data: TokenStats[];
}

export interface PoolToken {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  price: string;
}

export interface RewardPool {
  pool_id: number;
  pool_address: string;
  pool_name: string;
  pool_type: "CrossPool" | "GamePool";
  pool_status: string;
  deposit_token: PoolToken;
  reward_tokens: PoolToken[];
  total_deposited: string;
  total_users: number;
  created_block: number;
  created_time: number;
  last_updated_block: number;
  last_updated_time: number;
}

export interface RewardPoolsResponse {
  code: number;
  message: string;
  data: {
    pools: RewardPool[];
    total: number;
    has_next: boolean;
    page: number;
    page_size: number;
  };
}

export interface UserDepositInfo {
  account: string;
  pool_address: string;
  pool_id: number;
  pool_name: string;
  deposited_amount: string;
  claimable_reward: string;
  total_rewards_claimed: string;
  last_deposited_block: number;
  last_updated_block: number;
  last_updated_time: number;
  last_withdrawn_block: number;
}

export interface UserDepositsResponse {
  code: number;
  message: string;
  data: {
    user_deposits: UserDepositInfo[];
    total: number;
  };
}

/**
 * 사용자가 출금할 수 있는 예치 포지션 하나 (deposited > 0인 풀).
 * `useWithdrawPositions`가 deposits API + pools API(+온체인 메타 폴백)를
 * 합성해 만든다. 금액은 raw(wei) 문자열로 유지하고 표기 시에만 환산한다.
 */
export interface WithdrawPosition {
  poolId: number;
  poolAddress: string;
  /** pools API가 모르는(비활성/quest) 풀은 "Unknown". */
  poolType: RewardPool["pool_type"] | "Unknown";
  tokenSymbol: string;
  tokenAddress: string;
  decimals: number;
  /** USD 단가 (pools API 제공 시에만). */
  price?: string;
  /** 예치 잔액 (raw, wei 문자열) */
  depositedRaw: string;
}

/**
 * reward API `GET /v1/factory/info` 응답. 내장 Withdraw가 트랜잭션을 보낼
 * `CrossGameRewardRouter` 주소(`router_address`)를 여기서 얻는다 —
 * env별 주소 하드코딩 대신 백엔드가 단일 출처.
 */
export interface RewardFactoryInfo {
  active_pool_count: number;
  factory_address: string;
  game_pool_implementation: string;
  initialized_at: number;
  next_pool_id: number;
  pool_implementation: string;
  router_address: string;
  total_pool_count: number;
  wcross_address: string;
}

export interface RewardFactoryInfoResponse {
  code: number;
  message: string;
  data: RewardFactoryInfo;
}

/* ── Game-swap LP types ── */

/**
 * host(wagmi 보유 측)가 공급하는 LP 잔고 reader가 반환하는 단위 정보.
 * 금액은 모두 raw(wei) BigInt로 다루고, 표기 시에만 decimals로 환산한다.
 */
export interface LpBalanceInfo {
  /** 연결 계정의 LP 토큰 잔고 `balanceOf(account)` (raw, wei) */
  balance: bigint;
  /** LP 토큰 총발행량 `totalSupply()` (raw, wei). 없으면 지분율 계산 불가. */
  totalSupply?: bigint;
  /** LP 토큰 decimals (보통 18). 없으면 18로 간주. */
  decimals?: number;
}

/**
 * 주입형 LP 잔고 reader. host(wagmi)가 연결 계정 기준으로 주어진 pair(LP)
 * 토큰 주소들의 balanceOf / totalSupply / decimals를 읽어 반환한다.
 * 반환 맵의 key는 소문자 정규화된 pair 주소.
 *
 * dapp-ui는 이 함수 시그니처에만 의존하며 viem/wagmi를 직접 사용하지 않는다.
 * 미주입 시 dapp-ui가 CROSS 체인 RPC로 직접 읽는 내부 폴백
 * (`readLpBalancesViaRpc`)으로 동작하며, 폴백마저 실패해야 풀 정보만 표시한다.
 */
export type LpBalanceReaderFn = (
  pairAddresses: string[],
) => Promise<Record<string, LpBalanceInfo>>;

/**
 * game-swap `/portfolio` pool 항목의 토큰 참조.
 * NOTE: CROSS 쪽(token_b)은 백엔드가 메타데이터 없이 내려준다 —
 * `symbol: ""`, `name: ""`, `decimals: 0`, `logo_url` 누락. 소비 측에서
 * symbol은 "ONE"으로, decimals는 18로 간주해야 한다 (실응답 확인 기준).
 */
export interface GameSwapTokenRef {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logo_url?: string;
}

/**
 * game-swap `GET /addresses/:address/portfolio`의 `pools[]` 항목.
 * 풀 단위 정보(예비량/가격)만 담고, 사용자별 LP 잔고는 포함하지 않는다
 * (그것은 주입형 reader의 온체인 read로 보강한다).
 */
export interface GameSwapPool {
  pair_address: string;
  token_a?: GameSwapTokenRef;
  token_b?: GameSwapTokenRef;
  /** raw wei */
  reserve_a: string;
  /** raw wei */
  reserve_b: string;
  /** 1 token_a = X token_b(CROSS) */
  last_price: string;
  /** last_price × quote_usd */
  price_usd: string;
  /** CROSS/USD 환율 (백엔드 신규 필드명). */
  quote_usd?: string;
  /** @deprecated 백엔드가 `quote_usd`로 이관. 전환기 폴백용으로만 유지. */
  cross_usd?: string;
  /** token_a 24h 변동률 (0.0012 = +0.12%) */
  change_24h: string;
}

/**
 * game-swap `GET /addresses/:address/portfolio` 응답 body.
 * envelope 없이 `{ holdings, pools }`가 최상위로 내려온다
 * (cross-game-swap 레포 `TypesPortfolioResponseDTO` 및 실응답 확인 기준).
 */
export interface GameSwapPortfolioResponse {
  holdings?: unknown[];
  pools?: GameSwapPool[];
}

/* ── Staking (POSA) types ── */

/**
 * POSA governance API의 staking 요약 정보. `GET /stake/:address`.
 * 금액은 wei가 아니라 CROSS 단위 십진 문자열로 내려온다
 * (예: `"500.0000"`, `"0.000000000000000000"`). 실응답 확인 기준.
 */
export interface StakeInfo {
  address: string;
  /** 현재 스테이킹된 총량 (CROSS 단위 십진 문자열) */
  total_stake: string;
  /** 누적/청구 가능 리워드 (CROSS 단위 십진 문자열) */
  rewards_earned: string;
  /** 위임(delegation) 개수 */
  delegations: number;
  /** 최초 스테이킹 시각 (ISO timestamp). 스테이킹 이력이 없으면 null. */
  first_staked_at: string | null;
  /** 최초 스테이킹 이후 경과 일수. 스테이킹 이력이 없으면 null. */
  days_since_first_stake: number | null;
}

/** `GET /stake/:address` 응답 envelope. */
export interface StakeInfoResponse {
  code: number;
  message?: string;
  data: StakeInfo;
}

/** POSA `GET /rewards/:address` 일자별 리워드 레코드 중 포트폴리오에서 쓰는 필드. */
export interface StakeRewardRecord {
  block_timestamp: string;
  reference_date: string;
  /** 해당 스냅샷에 적립된 리워드 (CROSS 단위 십진 문자열) */
  earned: string;
  /** 누적 리워드 (CROSS 단위 십진 문자열) */
  cumulative_earned: string;
}

/** `GET /rewards/:address` 응답 envelope. 레코드는 최신순으로 내려온다. */
export interface StakeRewardsResponse {
  code: number;
  message?: string;
  data: {
    data: StakeRewardRecord[];
    limit?: number;
    page?: number;
    total?: number;
    total_pages?: number;
  };
}

/**
 * 주입형 staking 리워드 reader가 반환하는 온체인 값.
 * 금액은 모두 raw(wei) BigInt로 다루고, 표기 시에만 decimals(18)로 환산한다.
 */
export interface StakingRewardsInfo {
  /**
   * 온체인 누적 미청구 리워드 `earned(account)` (raw, wei).
   * posa 비활성 상태에서는 lastRewardBlock 이후 블록 기반 projection을 더한
   * 추정치(= posa 포트폴리오 "Accrued Rewards"와 동일 값)를 반환한다.
   */
  earnedWei: bigint;
  /** 온체인 스테이킹 원금 `balanceOf(account)` (raw, wei). 개인 APR 분모. */
  stakedWei: bigint;
}

/**
 * 주입형 staking 리워드 reader. host(wagmi 보유 측)가 연결 계정 기준으로
 * POSA delegation pool 컨트랙트의 `earned()` / `balanceOf()`를 읽어 반환한다.
 * dapp-ui는 viem/wagmi를 직접 쓰지 못하므로, posa 포트폴리오의 온체인
 * "Accrued Rewards"와 동일한 수치를 표기하려면 이 reader가 주입돼야 한다.
 * 미주입 시 StakeSection은 API `cumulative_earned`로 폴백한다.
 *
 * dapp-ui는 이 함수 시그니처에만 의존한다.
 */
export type StakingRewardsReaderFn = (
  account: string,
) => Promise<StakingRewardsInfo>;

/** 네트워크 통계 중 포트폴리오에서 사용하는 필드 (`GET /network/stats`). */
export interface NetworkStats {
  /** 네트워크 APR (백분율 문자열, 예: "12.5") */
  apr: string;
  total_staked: string;
  active_validators: number;
}

/** `GET /network/stats` 응답 envelope. */
export interface NetworkStatsResponse {
  code: number;
  message?: string;
  data: NetworkStats;
}

/* ── Forge pair (LP) types ── */

export interface ForgePoolToken {
  address: string;
  name: string;
  symbol: string;
  image: string;
}

export interface ForgePool {
  lp_balance: string;
  pair_address: string;
  pool_ownership: string;
  total_supply: string;
  token: ForgePoolToken;
}

export interface ForgePoolsResponse {
  success?: boolean;
  data: {
    items: ForgePool[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
    };
  };
}

export interface ForgeTokenDetail {
  address: string;
  name: string;
  symbol: string;
  image: string;
  image_url: string;
  pair_address: string;
  wrapped_native: string;
  virtual_reserve_b: string;
  reserve_a: string;
  reserve_b: string;
  graduated: boolean;
  current_price: string;
  market_cap: string;
  total_supply: string;
  available_supply: string;
}

export interface ForgeTokenDetailResponse {
  success?: boolean;
  data: {
    token: ForgeTokenDetail;
  };
}

/* ── CROSSD v3 pool types ── */

/** cross-defi API 토큰 참조 (detail 응답의 pool.token0/token1). */
export interface CrossdPositionTokenRef {
  address?: string;
  symbol?: string;
  name?: string;
  decimals?: number;
  logo_url?: string;
}

/** positions list 항목의 pool 요약 — 심볼만 있고 토큰 주소는 없다(detail에서 보강). */
export interface CrossdPositionPoolRef {
  pool_address?: string;
  fee_tier?: number;
  fee_tier_percentage?: string;
  token0_symbol?: string;
  token1_symbol?: string;
}

/**
 * cross-defi `GET /api/v1/positions?owner=` 목록 항목 중 포트폴리오가 쓰는 필드.
 * 수량류(liquidity, *_amount, uncollected_*)는 십진 문자열로 내려온다 —
 * 계산에 쓰는 것은 liquidity(BigInt 변환)뿐이고 나머지는 표시 전용.
 */
export interface CrossdPosition {
  token_id?: string;
  /** 0이면 전량 출금된(closed) 포지션 — 섹션에서 제외. */
  liquidity?: string;
  in_range?: boolean;
  is_full_range?: boolean;
  token0_amount?: string;
  token1_amount?: string;
  uncollected_fees_token0?: string;
  uncollected_fees_token1?: string;
  uncollected_fees_usd?: string;
  position_value_usd?: string;
  pool?: CrossdPositionPoolRef;
}

export interface CrossdPositionsResponse {
  positions?: CrossdPosition[];
  /** 백엔드가 envelope로 감싸는 경우 대비한 방어적 형태. */
  data?: { positions?: CrossdPosition[] };
}

/**
 * `GET /api/v1/positions/{token_id}` detail 중 출금에 필요한 부분.
 * list에는 없는 token0/token1의 주소·decimals를 여기서 얻는다
 * (WCROSS 판별 + sweepToken 인자 + 수량 표기).
 */
export interface CrossdPositionDetail {
  token_id?: string;
  liquidity?: string;
  pool?: {
    pool_address?: string;
    token0?: CrossdPositionTokenRef;
    token1?: CrossdPositionTokenRef;
  };
}

export interface CrossdPositionDetailResponse {
  data?: CrossdPositionDetail;
}
